import { Component, computed, inject, ChangeDetectorRef, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import {
  ChartComponent,
  ApexNonAxisChartSeries,
  ApexChart,
  ApexPlotOptions,
  ApexDataLabels,
  ApexFill,
  ApexStroke,
  ApexLegend,
  ApexAxisChartSeries,
  ApexXAxis,
  ApexYAxis,
  ApexMarkers,
  ApexGrid,
  ApexTooltip,
  ApexStates
} from 'ng-apexcharts';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { InvitationsService } from 'core/services/invitations.service';
import { AuthService } from 'core/services/auth.service';
import { HideApexChartTooltipOnScrollDirective } from 'shared/directives';
import { RefreshOnLanguageChangeDirective } from 'shared/directives/refresh-on-language-change.directive';
import { NO_PERSISTENT_CHART_SELECTION_EVENTS, NO_PERSISTENT_CHART_SELECTION_STATES } from 'shared/types/chart.config';
import { DASHBOARD_CHART_INTERACTION_EVENTS } from '../dashboard-chart-interactions';
import { ResourceLoadErrorComponent } from 'shared/components/resource-load-error/resource-load-error.component';

export type DonutChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  colors: string[];
  plotOptions: ApexPlotOptions;
  dataLabels: ApexDataLabels;
  fill: ApexFill;
  stroke: ApexStroke;
  legend: ApexLegend;
  tooltip: ApexTooltip;
  labels: string[];
  states: ApexStates;
};

export type LineChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  stroke: ApexStroke;
  dataLabels: ApexDataLabels;
  markers: ApexMarkers;
  colors: string[];
  grid: ApexGrid;
  legend: ApexLegend;
  states: ApexStates;
};

@Component({
  selector: 'app-community-growth',
  imports: [TranslatePipe, ChartComponent, HideApexChartTooltipOnScrollDirective, ResourceLoadErrorComponent],
  templateUrl: './community-growth.component.html',
  styleUrl: './community-growth.component.scss',
  hostDirectives: [RefreshOnLanguageChangeDirective]
})
export class CommunityGrowthComponent {
  #translateService = inject(TranslateService);
  #invitationsService = inject(InvitationsService);
  #authService = inject(AuthService);
  #changeDetectorRef = inject(ChangeDetectorRef);
  readonly #currentLanguage = toSignal(this.#translateService.onLangChange.pipe(map(event => event.lang)), {
    initialValue: this.#translateService.currentLang || this.#translateService.defaultLang || 'en'
  });

  constructor() {
    effect(() => {
      this.donutChartOptions();
      this.lineChartOptions();
      this.#changeDetectorRef.markForCheck();
    });
  }

  referralGrowthResource = this.#invitationsService.getReferralGrowthChartResource();

  get currentLang(): string {
    return this.#currentLanguage();
  }

  // Total community size for donut chart center
  readonly totalCommunitySize = computed(
    () => this.#authService.currentUser?.communityBreakdown?.total_community_size ?? 0
  );

  // donutChart — 3 секции, как в Фигме: Ganarbols / Ganacuates / Ganasocios.
  // Сумма даёт total_community_size, как значение в центре.
  donutChartOptions = computed<DonutChartOptions>(() => {
    const breakdown = this.#authService.currentUser?.communityBreakdown?.breakdown;
    return {
      series: [breakdown?.ganarbols ?? 0, breakdown?.ganacuates ?? 0, breakdown?.ganasocios ?? 0],
      colors: ['#007DCB', '#8AC3E7', '#A22561'],
      chart: {
        type: 'donut',
        height: 250,
        width: 250,
        toolbar: { show: false },
        events: DASHBOARD_CHART_INTERACTION_EVENTS
      },
      dataLabels: {
        enabled: false
      },
      legend: {
        show: false
      },
      stroke: {
        width: 0
      },
      plotOptions: {
        pie: {
          expandOnClick: false,
          donut: {
            size: '60%'
          }
        }
      },
      fill: {
        opacity: 1
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: function (val: number) {
            return val.toString();
          }
        }
      },
      labels: ['Ganarbols', 'Ganacuates', 'Ganasocios'],
      states: NO_PERSISTENT_CHART_SELECTION_STATES
    };
  });

  // lineChart — 3 серии, как в Фигме: Sent / Registered / Card activations
  lineChartOptions = computed<LineChartOptions>(() => {
    const data = this.referralGrowthResource.hasValue() ? this.referralGrowthResource.value() || [] : [];
    const categories = data.map(item => item.month);
    const sentData = data.map(item => item.sent);
    const registeredData = data.map(item => item.registered);
    const cardActivationsData = data.map(item => item.card_activations);

    return {
      series: [
        { name: this.currentLang === 'en' ? 'Sent' : 'Enviadas', data: sentData },
        { name: this.currentLang === 'en' ? 'Registered' : 'Registrados', data: registeredData },
        {
          name: this.currentLang === 'en' ? 'Card activations' : 'Activaciones de tarjetas',
          data: cardActivationsData
        }
      ],
      chart: {
        type: 'line',
        height: 250,
        zoom: { enabled: false },
        toolbar: { show: false },
        events: NO_PERSISTENT_CHART_SELECTION_EVENTS
      },
      stroke: {
        curve: 'smooth',
        width: 3
      },
      colors: ['#EF6401', '#007DCB', '#0D9443'],
      dataLabels: {
        enabled: false
      },
      markers: {
        size: 0
      },
      grid: {
        borderColor: '#E7E7E7',
        row: {
          colors: ['transparent'],
          opacity: 0.5
        }
      },
      yaxis: {
        labels: {
          style: {
            colors: '#8A8E94',
            fontSize: '0.9375rem',
            fontFamily: 'Outfit, sans-serif'
          }
        }
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            colors: '#8A8E94',
            fontSize: '1.125rem',
            fontFamily: 'Outfit, sans-serif'
          }
        }
      },
      legend: {
        show: false
      },
      states: NO_PERSISTENT_CHART_SELECTION_STATES
    };
  });
}
